import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { roomTitle } from '../components/Together/roomAppearance';
import { isUrgent, nextStep } from '../components/Together/roomUi';

// Same model as the mobile app: friend requests, rooms that need you right now, and rooms you were added to.
export type AppNotification = { id: string; type: 'friend-request' | 'room-action' | 'room-added'; title: string; body: string; cta?: string; createdAt?: string; urgent?: boolean; data: any };
type Value = { items: AppNotification[]; rooms: any[]; liveRooms: any[]; unreadCount: number; refresh: () => Promise<void>; markRead: (id: string) => void; respond: (item: AppNotification, accept: boolean) => Promise<void>; alertsEnabled: boolean; enableAlerts: () => Promise<void> };

const seenKey = 'find-a-film:seen-notifications';
const readSeen = (): string[] => { try { return JSON.parse(localStorage.getItem(seenKey) || '[]'); } catch { return []; } };
const Context = createContext<Value | null>(null);
const actionKey = (room: any) => `${room.sessionId}:${room.round?.id || 'legacy'}:${room.myAction}`;
const canNotify = () => typeof window !== 'undefined' && 'Notification' in window;

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [alertsEnabled, setAlertsEnabled] = useState(() => canNotify() && Notification.permission === 'granted');
  const knownActions = useRef<Set<string> | null>(null);

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); setRooms([]); knownActions.current = null; return; }
    const [requestData, sessionData] = await Promise.all([apiRequest<any>('/connections/requests'), apiRequest<any>('/sessions')]);
    const seen = readSeen();
    const roomList: any[] = sessionData.items || [];
    const requests: AppNotification[] = (requestData.items || []).map((request: any) => ({ id: `friend:${request.otherUserId}`, type: 'friend-request', title: `${request.otherName} sent you a friend request`, body: 'Accept to start film nights together.', createdAt: request.requestedAt || request.createdAt, data: request }));
    const actions: AppNotification[] = roomList.filter(isUrgent).map(room => { const step = nextStep(room); return { id: `room:${actionKey(room)}`, type: 'room-action', title: roomTitle(room), body: step.text, cta: step.cta, urgent: step.tone === 'urgent', createdAt: room.round?.startedAt || room.createdAt, data: room }; });
    const added: AppNotification[] = roomList
      .filter(room => room.createdBy && room.createdBy !== user.userId && !isUrgent(room) && !seen.includes(`together:${room.sessionId}`))
      .map(room => ({ id: `together:${room.sessionId}`, type: 'room-added', title: `You were added to ${roomTitle(room)}`, body: 'Start a film night whenever you’re ready.', cta: 'Open room', createdAt: room.createdAt, data: room }));
    setRooms(roomList);
    setItems([...actions, ...requests, ...added].sort((a, b) => Number(Boolean(b.urgent)) - Number(Boolean(a.urgent)) || (b.createdAt || '').localeCompare(a.createdAt || '')));

    // A browser alert when something new needs you while this tab is in the background.
    const current = new Set(roomList.filter(isUrgent).map(actionKey));
    if (knownActions.current && document.hidden && canNotify() && Notification.permission === 'granted') {
      roomList.filter(room => isUrgent(room) && !knownActions.current!.has(actionKey(room))).forEach(room => {
        const step = nextStep(room);
        const alert = new Notification(roomTitle(room), { body: `${step.text} · ${step.cta}`, tag: actionKey(room) });
        alert.onclick = () => { window.focus(); window.location.assign(`/together?room=${room.sessionId}`); };
      });
    }
    knownActions.current = current;
  }, [user]);

  const markRead = (id: string) => {
    if (!id.startsWith('together:')) return;
    const seen = readSeen();
    if (!seen.includes(id)) localStorage.setItem(seenKey, JSON.stringify([...seen, id].slice(-100)));
    setItems(current => current.filter(item => item.id !== id));
  };

  const respond = async (item: AppNotification, accept: boolean) => {
    await apiRequest(`/connections/requests/${encodeURIComponent(item.data.otherUserId)}`, { method: accept ? 'PUT' : 'DELETE', ...(accept ? { body: '{}' } : {}) });
    await refresh();
  };

  const enableAlerts = async () => { if (!canNotify()) return; setAlertsEnabled((await Notification.requestPermission()) === 'granted'); };

  // Poll faster while a film night is under way so countdowns and calls to action stay current.
  const busy = rooms.some(room => ['answering', 'stalled', 'ready', 'shortlist'].includes(room.phase));
  useEffect(() => {
    refresh().catch(() => undefined);
    if (!user) return;
    const interval = window.setInterval(() => refresh().catch(() => undefined), busy ? 10000 : 30000);
    const onFocus = () => refresh().catch(() => undefined);
    window.addEventListener('focus', onFocus);
    return () => { window.clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [refresh, user, busy]);

  return <Context.Provider value={{ items, rooms, liveRooms: rooms.filter(isUrgent), unreadCount: items.length, refresh, markRead, respond, alertsEnabled, enableAlerts }}>{children}</Context.Provider>;
}

export const useNotifications = () => {
  const value = useContext(Context);
  if (!value) throw new Error('NotificationProvider missing');
  return value;
};
